import type { ComponentType, SVGProps } from 'react';
import { ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { AttachmentNotice, ComposerAttachment } from './useComposerAttachment';
import { StagedAttachmentChip } from './StagedAttachmentChip';

interface NoticeTone {
    role: 'alert' | 'status';
    Icon: ComponentType<SVGProps<SVGSVGElement>>;
    container: string;
    dismiss: string;
}

const NOTICE_TONES: Record<AttachmentNotice['tone'], NoticeTone> = {
    error: {
        role: 'alert',
        Icon: ExclamationCircleIcon,
        container: 'bg-red-50 text-red-700',
        dismiss: 'text-red-400 hover:bg-red-100 hover:text-red-700'
    },
    info: {
        role: 'status',
        Icon: InformationCircleIcon,
        container: 'bg-gray-50 text-gray-600',
        dismiss: 'text-gray-400 hover:bg-gray-200 hover:text-gray-700'
    }
};

function NoticeLine({ notice, onDismiss }: { notice: AttachmentNotice; onDismiss: () => void }) {
    const tone = NOTICE_TONES[notice.tone];
    return (
        <div
            role={tone.role}
            className={`flex items-start gap-1.5 rounded-lg py-1.5 pl-2 pr-1 text-xs leading-4 ${tone.container}`}
        >
            <tone.Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="flex-1 min-w-0 break-words">{notice.text}</span>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className={`-my-0.5 w-5 h-5 flex-shrink-0 rounded flex items-center justify-center transition-colors ${tone.dismiss}`}
            >
                <XMarkIcon className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}

function ComposerAttachmentArea({ attachment }: { attachment: ComposerAttachment }) {
    const { staged, notice, clear, dismissNotice } = attachment;
    if (!staged && !notice) return null;
    return (
        <div className="flex flex-col gap-1.5 min-w-0 px-2 pt-2">
            {staged && <StagedAttachmentChip staged={staged} onRemove={clear} />}
            {notice && <NoticeLine notice={notice} onDismiss={dismissNotice} />}
        </div>
    );
}

export { ComposerAttachmentArea };
